import type { TFunction } from "i18next";
import * as mammoth from "mammoth";
import { htmlStringToPdfBlobRaster } from "./html-to-pdf";

type Deps = {
  mammothApi?: Pick<typeof mammoth, "convertToHtml" | "images">;
  htmlToPdf?: typeof htmlStringToPdfBlobRaster;
};

/**
 * Convert a .docx file to a PDF in the browser.
 *
 * How it works:
 * 1) Use Mammoth to convert the DOCX to HTML.
 * 2) Inline any images as data URLs (base64) so rendering works offline.
 * 3) Render the HTML to a PDF using our "HTML -> screenshot -> PDF" flow.
 */
export async function convertDocxToPdfBlob(file: File, t: TFunction, deps: Deps = {}): Promise<Blob> {
  // Dependency injection so we can mock Mammoth / HTML->PDF in tests.
  const mammothApi = deps.mammothApi ?? mammoth;
  const htmlToPdf = deps.htmlToPdf ?? htmlStringToPdfBlobRaster;

  // Read the DOCX into memory for Mammoth.
  const arrayBuffer = await file.arrayBuffer();

  // Convert DOCX -> HTML.
  // We also convert embedded images to base64 data URLs so they appear in the output.
  const result = await mammothApi.convertToHtml(
    { arrayBuffer },
    {
      convertImage: mammothApi.images.imgElement(async (image) => {
        const b64 = await image.readAsBase64String();
        return { src: `data:${image.contentType};base64,${b64}` };
      }),
    }
  );

  // Convert the HTML into a PDF (raster/screenshot style).
  return htmlToPdf(
    {
      titleText: file.name,
      bodyHtml: result.value,
    },
    t
  );
}
